import { LicensePlateNumber } from '@products/models/parking.model';
import { ProductType } from '@products/models/products.model';
import { BookingPromoCodes, PromoCode } from '@products/models/smart-promotions';
import { DocumentUploadType, TariffPersonalizationType, TariffUuid, TariffUuids } from '@products/models/tariff.model';
import { VoucherCode, VoucherType } from '@products/models/voucher.model';
import { WorkshopAvailableSeats } from '@products/models/workshop-status.model';

export interface BookingState {
  list: BookedProducts;
  bookedTariffUuids: TariffUuids;
  countdown: BookingCountdown;
  claimedTicketHash: string;
  isClaimedTicketHashValid: boolean;
  isAnonymousProductSentToAPI: boolean;
  promoCodes: BookingPromoCodes;
}

export interface BookingCountdown {
  product?: number;
  voucher?: VoucherBookingCountdown[];
}

export type BookedProducts = BookingProductType[];

export type BookingProductType = BookingTariffType | BookingPackageType | BookingArticleType;

export interface BookingTariffType {
  productType: ProductType.Tariff;
  productTypeName: string;
  ticketTypeId: number;
  tariffs: BookingTariff[];
}

export interface BookingPackageType {
  productType: ProductType.Package;
  productTypeName: string;
  packageNumber: number;
  packageIndex: number;
  productTypes: BookingProductType[];
}

export interface BookingArticleType {
  productType: ProductType.Article;
  productTypeName: string;
}

export interface BookingWorkshop {
  id: number;
  name: string;
  price: number;
  tariffUuids: TariffUuids;
}

export interface BookingContingentReservation {
  tariffUuid: TariffUuid;
  duration: number;
  isValid: boolean;
  fromDate: Date;
  toDate: Date;
}

export interface BookingAmanoParkingReservation {
  isValid: boolean;
  since: Date;
  until: Date;
  price: number;
}

export interface BookingParkingReservation {
  tariffUuid: TariffUuid;
  isValid: boolean;
  licensePlateNumber: LicensePlateNumber;
}

export interface SetBookingTariff {
  ticketPersonTypeId: number;
  tariffNumber: number;
  ticketTypeName: string;
  ticketName: string;
  packageNumber: number;
  packageIndex: number;
  tariffUuids: TariffUuids;
  availableTickets: number;
  ticketLimit: number;
  count: number;
  price: number;
  voucherCode: VoucherCode;
  voucherType: VoucherType;
  voucherCountLimit?: number;
  activatedTimestamp?: number;
  personalizationType: TariffPersonalizationType;
  documentUploadType: DocumentUploadType;
  workshops: BookingWorkshop[];
  contingentReservations: BookingContingentReservation[];
  amanoParkingReservations: BookingAmanoParkingReservation[];
  parkingReservations: BookingParkingReservation[];
}

export interface PromoCodeDiscounts {
  [promoCode: PromoCode]: { discountCount: number; discountPercentage: number };
}

export interface SetPromoCodeBookingTariff {
  discountedPrice: number;
  discountPercentage: number;
  promoCodeDiscounts: PromoCodeDiscounts;
}

export interface BookingTariff extends SetBookingTariff, SetPromoCodeBookingTariff {
  ticketTypeId: number;
  ticketPersonId: number;
}

export interface RedeemPromoCodeBookingTariff extends BookingTariff {
  redeemPromoCodeTicketIndexes: number[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface BookingArticle {}

export interface RemoveProductCartBooking {
  ticketTypeId: number;
  ticketPersonId: number;
  packageId?: number;
  packageIndex?: number;
}

export interface SetBookingContingentReservation extends BookingContingentReservation {
  bookingReservationIndex: number;
}

export interface SetProductBookingContingentReservation {
  productType: ProductType;
  ticketTypeId: number;
  ticketPersonId: number;
  ticketPersonTypeId: number;
  voucherCode?: VoucherCode;
  packageNumber?: number;
  packageIndex?: number;
  contingentReservations: SetBookingContingentReservation[];
}

export interface RemoveProductBookingContingentReservation {
  productType: ProductType;
  ticketTypeId: number;
  ticketPersonId: number;
  voucherCode?: VoucherCode;
  packageNumber?: number;
  packageIndex?: number;
  removeBookingReservationIndexes: number[];
}

export interface SetProductBookingAmanoParkingReservation {
  productType: ProductType;
  ticketTypeId: number;
  ticketPersonId: number;
  voucherCode?: VoucherCode;
  packageNumber?: number;
  packageIndex?: number;
  bookingReservationIndex: number;
  price: number;
  isValid: boolean;
}

export interface SetProductBookingParkingReservation {
  productType: ProductType;
  ticketTypeId: number;
  ticketPersonId: number;
  voucherCode?: VoucherCode;
  packageNumber?: number;
  packageIndex?: number;
  bookingReservationIndex: number;
  licensePlateNumber: LicensePlateNumber;
  isValid: boolean;
}

export interface ProductTariffUuid {
  tariffUuid: TariffUuid;
  productType: ProductType;
  ticketTypeId: number;
  ticketPersonId: number;
  packageNumber?: number;
  packageIndex?: number;
}

export interface PostProductBooking {
  ticketPersonId: number;
  ticketTypeId: number;
  groupId: number;
  eventId: number;
  count: number;
  uuid: string;
  voucherCode: VoucherCode;
}

export interface PostProductBookingResponse {
  groupId: number;
  ticketTypeId: number;
  availableTickets: number;
  sellLimit: number;
  ticketPersonId: number;
}

export interface PostWorkshopBooking {
  eventId: number;
  workshopId: number;
  seats: number;
  uuid: string;
  tariffUuid?: string;
}

export interface PostWorkshopResponse {
  workshopId: number;
  seats: number;
}

export interface SetBookingWorkshop {
  productType: ProductType;
  ticketTypeId: number;
  ticketPersonId: number;
  voucherCode?: VoucherCode;
  packageNumber?: number;
  packageIndex?: number;
  workshops: BookingWorkshop[];
}

export interface SetWorkshopBookingsAndSetSeats {
  setBookingWorkshops: SetBookingWorkshop[];
  workshopAvailableSeats: WorkshopAvailableSeats;
}

export interface RemoveBookingWorkshopAssignedHolderTariffs {
  workshopId: number;
  removeTariffUuids: TariffUuids;
}

export interface RemoveBookingWorkshop {
  productType: ProductType;
  ticketTypeId: number;
  ticketPersonId: number;
  voucherCode?: VoucherCode;
  packageNumber?: number;
  packageIndex?: number;
  removeWorkshopsAssignedHolderTariffs: RemoveBookingWorkshopAssignedHolderTariffs[];
}

export interface RemoveWorkshopBookingsAndSetSeats {
  removeBookingWorkshops: RemoveBookingWorkshop[];
  workshopAvailableSeats: WorkshopAvailableSeats;
}

export interface BookedVoucherCountSortedByVoucherType {
  [VoucherType.PromoCode]: number;
  [VoucherType.LimiterPromoCode]: number;
  [VoucherType.OneTimeVoucher]: number;
}

export interface SharedBookedPersonIdTariffsTotalCount {
  [ticketPersonId: number]: number;
}

export interface BookedPackageTotalPriceGroupedByPackageNumber {
  [packageNumber: number]: { totalPrice: number; totalDiscountedPrice: number };
}

export interface BookedPackageTotalPriceGroupedByPackageIndex {
  [packageIndex: number]: { totalPrice: number; totalDiscountedPrice: number; hasPromoCodeDiscount: boolean };
}

export interface AllBookedProductsTotalPrice {
  totalPrice: number;
  totalDiscountedPrice: number;
}

export interface VoucherBookingCountdown {
  voucherCode: string;
  expiryTimestamp: number;
  isAnonymous?: boolean;
}

/**************
 * TYPE GUARDS
 **************/
export const isBookingProductTypeTariff = (
  bookingProductType: BookingProductType
): bookingProductType is BookingTariffType => {
  const tariffProductType = bookingProductType as BookingTariffType;
  return tariffProductType.productType === ProductType.Tariff;
};

export const isBookingTariffVoucher = (bookedTariff: BookingTariff) => {
  return !!bookedTariff.voucherCode;
};

export const isBookingProductTypePackage = (
  bookingProductType: BookingProductType
): bookingProductType is BookingPackageType => {
  const packageProductType = bookingProductType as BookingPackageType;
  return packageProductType.productType === ProductType.Package;
};

export const isBookingProductTypeArticle = (
  bookingProductType: BookingProductType
): bookingProductType is BookingArticleType => {
  const articleProductType = bookingProductType as BookingArticleType;
  return articleProductType.productType === ProductType.Article;
};
